import React, { useEffect, useRef, useState } from "react";
import { styled } from "styled-components";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "react-query";
import Section from "@cs/components/src/Section";
import Spinner from "@cs/components/src/Spinner";
import Banner from "@cs/components/src/Banner";
import Button from "@cs/components/src/Button";
import { exchangeLoginCode } from "../../services/sessionExchange";

const Wrapper = styled.div`
  max-width: 500px;
  width: 90%;
  margin: auto;
  margin-top: 50px;
`;

/**
 * DORMANT since 2026-07-07 — not linked to from anywhere in the UI (RequestPinScreen now shows
 * the password+2FA form instead of a "Login with iconik" button; see SessionService.
 * createAuthorizeRedirect's doc comment for why this is kept rather than deleted). Left routed
 * at /auth/callback in AppRouter so re-enabling OAuth later is just re-adding the button.
 *
 * Lands after the backend's /api/session/oauth/callback redirects here with a short-lived,
 * single-use ?login_code= (never the real session id — see SessionService.handleOAuthCallback).
 * Immediately exchanges it for the real session, stores it, then replaces the URL so the code
 * never lingers in browser history / gets resent via a referrer header.
 */
const OAuthCallbackScreen = () => {
  const [error, setError] = useState<string>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const exchanged = useRef(false);

  useEffect(() => {
    if (exchanged.current) {
      return;
    }
    exchanged.current = true;

    const loginCode = new URLSearchParams(window.location.search).get(
      "login_code"
    );
    if (!loginCode) {
      setError("Missing login code.");
      return;
    }

    exchangeLoginCode(loginCode)
      .then(() => {
        queryClient.invalidateQueries(["session"]);
        navigate("/", { replace: true });
      })
      .catch(() => {
        setError("That login link already expired. Please try again.");
      });
  }, [navigate, queryClient]);

  return (
    <Wrapper>
      <Section title="Iconik Manager" pad>
        {error ? (
          <>
            <Banner error={error} padBottom />
            <Button
              label="Back to login"
              type="success"
              onClick={() => navigate("/", { replace: true })}
            />
          </>
        ) : (
          <Spinner />
        )}
      </Section>
    </Wrapper>
  );
};

export default OAuthCallbackScreen;
